import { FC, useMemo } from 'react'
import {
  AISummaryWidget,
  AISummaryWidgetCopy,
} from 'src/components/AISummaryWidget'
import {
  AISummaryScope,
  GenerateAISummaryPayload,
} from 'src/interfaces/aiSummary.interface'
import { useGenerateAISummaryMutation } from 'src/reactQuery/useGenerateAISummaryMutation'
import { useGetAISummariesQuery } from 'src/reactQuery/useGetAISummariesQuery'
import {
  buildCalendarContext,
  CalendarContext,
} from 'src/utils/calendarContext'

const COPY: Record<AISummaryScope['kind'], AISummaryWidgetCopy> = {
  adPerformance: {
    emptyText:
      'Generate your first summary to see what drove your ad results and what to change next',
    footerText:
      'Summary generated by AI based on your ad account performance for the selected period',
    generatingText:
      "This may take a few moments. We're reading spend, results and audiences",
  },
  aiListeningProfile: {
    emptyText:
      'Generate your first summary to see how AI assistants describe and recommend this brand, and why',
    footerText:
      'Summary generated by AI based on AI assistant answers to your tracked prompts for the selected period',
    generatingText:
      "This may take a few moments. We're reading AI answers, recommendations and citations",
  },
  // Review Analysis helper texts carry no trailing period (QA, 2026-10-13).
  reviewAnalysis: {
    emptyText:
      'Generate your first summary to see AI-powered insights about what your customers are saying and why',
    footerText:
      'Summary generated by AI based on customer reviews for the selected period',
    generatingText:
      "This may take a few moments. We're scanning reviews, ratings and topics",
  },
  socialListening: {
    emptyText:
      'Generate your first summary to see the context, themes and reasons behind the mentions of your keywords.',
    footerText:
      'Summary generated by AI based on mentions of your keywords for the selected period.',
    generatingText:
      "This may take a few moments. We're scanning mentions, keywords and topics.",
  },
}

const REVIEW_FACILITY_COPY: AISummaryWidgetCopy = {
  ...COPY.reviewAnalysis,
  footerText:
    'Summary generated by AI based on customer reviews of this facility for the selected period',
}

/**
 * The Time Filter calendar's dots follow the summary's own scope, so the days
 * marked are exactly the days this summary can cover:
 *   - review analysis → that campaign's reviews, narrowed to the facility
 *   - social listening → that campaign's mentions
 * Built by the same helper as the top-bar calendar, so on the same page both
 * calendars mark the same days.
 */
export const summaryCalendarContext = (
  scope: AISummaryScope
): CalendarContext => {
  switch (scope.kind) {
    case 'reviewAnalysis':
      return buildCalendarContext({
        campaignId: scope.id,
        facilityId: scope.facilityId,
        kind: 'reviewAnalysis',
      })
    // A profile spans many prompts (campaigns): no single campaign's days.
    case 'aiListeningProfile':
      return buildCalendarContext({ kind: 'aiListening' })
    default:
      return buildCalendarContext({
        campaignId: scope.id,
        kind: 'socialListening',
      })
  }
}

interface ScopedSummarySectionProps {
  scope: AISummaryScope
}

// Review Analysis (facility page), Social Listening and AI Listening (brand /
// competitor profile) → Summary.
//
// Independent of the page's global date filter by design: the widget never
// pre-selects a period. The user picks an explicit start and end date in the
// widget's own Time Filter, and the list shows every stored period as a tab.
export const ScopedSummarySection: FC<ScopedSummarySectionProps> = ({
  scope,
}) => {
  const { data, isLoading, isError, refetch } = useGetAISummariesQuery(scope)
  const generateMutation = useGenerateAISummaryMutation({ scope })
  const { facilityId, id, kind, subject } = scope
  // Ad accounts have no data-days endpoint yet, so their calendar shows no dots.
  const calendar = useMemo(
    () =>
      kind === 'adPerformance'
        ? undefined
        : summaryCalendarContext({ facilityId, id, kind, subject }),
    [facilityId, id, kind, subject]
  )

  const handleGenerate = (payload: GenerateAISummaryPayload) =>
    generateMutation
      .mutateAsync(payload)
      .then((result) => result.data)
      .catch(() => undefined)

  return (
    <AISummaryWidget
      calendar={calendar}
      copy={
        scope.kind === 'reviewAnalysis' && scope.facilityId
          ? REVIEW_FACILITY_COPY
          : COPY[scope.kind]
      }
      data={data?.data}
      isError={isError}
      isGenerating={generateMutation.isPending}
      isLoading={isLoading}
      onGenerate={handleGenerate}
      onRetry={() => refetch()}
    />
  )
}
